// =============================================================================
// Canvas Store Factory — Shared between desktop and web
// =============================================================================
//
// Creates a Zustand store with all canvas state and actions. Platform-specific
// stores extend this with their own persistence and API integration.
// =============================================================================

import { create } from 'zustand';
import type { CanvasState, CanvasLayer, CanvasLayerType, CanvasTool, SizePreset, FlyerProject, SelectionData } from './types';
import { DEFAULT_CANVAS_STATE, MAX_HISTORY_STATES, MIN_ZOOM, MAX_ZOOM, DEFAULT_ZOOM, DEFAULT_GRID_SIZE } from './constants';
import { createLayer, reindexLayers, moveLayerToIndex, bringForward, sendBackward, bringToFront, sendToBack, duplicateLayer, duplicateLayers } from './layers';
import { HistoryManager } from './history';

// -----------------------------------------------------------------------------
// Store Interface
// -----------------------------------------------------------------------------

export interface CanvasStoreState {
  // Canvas data
  canvasState: CanvasState;
  selectedLayerIds: string[];  // Multi-select support
  activeTool: CanvasTool;

  // View
  zoom: number;
  showGrid: boolean;
  snapToGrid: boolean;
  gridSize: number;

  // Selection (magic wand, lasso, etc.)
  selection: SelectionData | null;

  // Clipboard
  clipboard: CanvasLayer[];

  // History (managed internally)
  canUndo: boolean;
  canRedo: boolean;
}

export interface CanvasStoreActions {
  // Canvas state
  setCanvasState: (state: CanvasState) => void;
  updateCanvasSize: (width: number, height: number) => void;
  applySizePreset: (preset: SizePreset) => void;

  // Layer CRUD
  addLayer: (options: Partial<Omit<CanvasLayer, 'id' | 'z_index'>> & { type: CanvasLayerType }) => string;
  updateLayer: (id: string, updates: Partial<CanvasLayer>) => void;
  deleteLayer: (id: string) => void;
  deleteLayers: (ids: string[]) => void;

  // Selection
  selectLayer: (id: string | null, addToSelection?: boolean) => void;
  selectLayers: (ids: string[]) => void;
  selectAll: () => void;
  deselectAll: () => void;

  // Layer ordering
  moveLayer: (id: string, newIndex: number) => void;
  bringForward: (id: string) => void;
  sendBackward: (id: string) => void;
  bringToFront: (id: string) => void;
  sendToBack: (id: string) => void;

  // Layer visibility & lock
  toggleLayerVisibility: (id: string) => void;
  toggleLayerLock: (id: string) => void;

  // Clipboard
  copyLayers: () => void;
  cutLayers: () => void;
  pasteLayers: () => void;
  duplicateSelected: () => void;

  // History
  saveToHistory: () => void;
  undo: () => void;
  redo: () => void;

  // View
  setZoom: (zoom: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  setShowGrid: (show: boolean) => void;
  setSnapToGrid: (snap: boolean) => void;
  resetView: () => void;
  setActiveTool: (tool: CanvasTool) => void;

  // Selection mask
  setSelection: (selection: SelectionData | null) => void;

  // Project
  loadProject: (project: FlyerProject) => void;
  getProjectState: () => CanvasState;
}

export type CanvasStore = CanvasStoreState & CanvasStoreActions;

// -----------------------------------------------------------------------------
// Store Factory
// -----------------------------------------------------------------------------

/**
 * Create a new canvas store instance.
 * Call this in each app with its own persistence setup.
 */
export function createCanvasStore() {
  // History manager lives outside the store to avoid serialization issues
  let history = new HistoryManager<CanvasState>(DEFAULT_CANVAS_STATE, MAX_HISTORY_STATES);

  return create<CanvasStore>((set, get) => ({
    // Initial state
    canvasState: DEFAULT_CANVAS_STATE,
    selectedLayerIds: [],
    activeTool: 'select',
    zoom: DEFAULT_ZOOM,
    showGrid: true,
    snapToGrid: true,
    gridSize: DEFAULT_GRID_SIZE,
    selection: null,
    clipboard: [],
    canUndo: false,
    canRedo: false,

    // Canvas state
    setCanvasState: (state) => set({ canvasState: state }),

    updateCanvasSize: (width, height) => {
      const { canvasState } = get();
      const newLayers = canvasState.layers.map((layer) =>
        layer.type === 'background' ? { ...layer, width, height } : layer
      );
      const newState = { ...canvasState, width, height, layers: newLayers };
      set({ canvasState: newState });
      get().saveToHistory();
    },

    applySizePreset: (preset) => {
      get().updateCanvasSize(preset.width, preset.height);
    },

    // Layer CRUD
    addLayer: (options) => {
      const { canvasState } = get();
      const newLayer = createLayer(options, canvasState.layers.length);
      const newState = {
        ...canvasState,
        layers: [...canvasState.layers, newLayer],
      };
      set({ canvasState: newState, selectedLayerIds: [newLayer.id] });
      get().saveToHistory();
      return newLayer.id;
    },

    updateLayer: (id, updates) => {
      const { canvasState } = get();
      const newLayers = canvasState.layers.map((layer) =>
        layer.id === id ? { ...layer, ...updates } : layer
      );
      set({ canvasState: { ...canvasState, layers: newLayers } });
    },

    deleteLayer: (id) => {
      const { canvasState, selectedLayerIds } = get();
      if (id === 'background') return;
      const newLayers = reindexLayers(canvasState.layers.filter((l) => l.id !== id));
      set({
        canvasState: { ...canvasState, layers: newLayers },
        selectedLayerIds: selectedLayerIds.filter((sid) => sid !== id),
      });
      get().saveToHistory();
    },

    deleteLayers: (ids) => {
      const { canvasState, selectedLayerIds } = get();
      const idsSet = new Set(ids);
      idsSet.delete('background');
      if (idsSet.size === 0) return;
      const newLayers = reindexLayers(canvasState.layers.filter((l) => !idsSet.has(l.id)));
      set({
        canvasState: { ...canvasState, layers: newLayers },
        selectedLayerIds: selectedLayerIds.filter((sid) => !idsSet.has(sid)),
      });
      get().saveToHistory();
    },

    // Selection
    selectLayer: (id, addToSelection = false) => {
      if (id === null) {
        set({ selectedLayerIds: [] });
        return;
      }
      if (addToSelection) {
        const { selectedLayerIds } = get();
        if (selectedLayerIds.includes(id)) {
          set({ selectedLayerIds: selectedLayerIds.filter((sid) => sid !== id) });
        } else {
          set({ selectedLayerIds: [...selectedLayerIds, id] });
        }
      } else {
        set({ selectedLayerIds: [id] });
      }
    },

    selectLayers: (ids) => set({ selectedLayerIds: ids }),

    selectAll: () => {
      const { canvasState } = get();
      const ids = canvasState.layers
        .filter((l) => l.type !== 'background' && l.visible && !l.locked)
        .map((l) => l.id);
      set({ selectedLayerIds: ids });
    },

    deselectAll: () => set({ selectedLayerIds: [] }),

    // Layer ordering
    moveLayer: (id, newIndex) => {
      const { canvasState } = get();
      const newLayers = moveLayerToIndex(canvasState.layers, id, newIndex);
      set({ canvasState: { ...canvasState, layers: newLayers } });
      get().saveToHistory();
    },

    bringForward: (id) => {
      const { canvasState } = get();
      const newLayers = bringForward(canvasState.layers, id);
      set({ canvasState: { ...canvasState, layers: newLayers } });
      get().saveToHistory();
    },

    sendBackward: (id) => {
      const { canvasState } = get();
      const newLayers = sendBackward(canvasState.layers, id);
      set({ canvasState: { ...canvasState, layers: newLayers } });
      get().saveToHistory();
    },

    bringToFront: (id) => {
      const { canvasState } = get();
      const newLayers = bringToFront(canvasState.layers, id);
      set({ canvasState: { ...canvasState, layers: newLayers } });
      get().saveToHistory();
    },

    sendToBack: (id) => {
      const { canvasState } = get();
      const newLayers = sendToBack(canvasState.layers, id);
      set({ canvasState: { ...canvasState, layers: newLayers } });
      get().saveToHistory();
    },

    // Layer visibility & lock
    toggleLayerVisibility: (id) => {
      const { canvasState } = get();
      const newLayers = canvasState.layers.map((layer) =>
        layer.id === id ? { ...layer, visible: !layer.visible } : layer
      );
      set({ canvasState: { ...canvasState, layers: newLayers } });
    },

    toggleLayerLock: (id) => {
      const { canvasState } = get();
      const newLayers = canvasState.layers.map((layer) =>
        layer.id === id ? { ...layer, locked: !layer.locked } : layer
      );
      set({ canvasState: { ...canvasState, layers: newLayers } });
    },

    // Clipboard
    copyLayers: () => {
      const { canvasState, selectedLayerIds } = get();
      const layers = canvasState.layers.filter((l) => selectedLayerIds.includes(l.id));
      set({ clipboard: structuredClone(layers) });
    },

    cutLayers: () => {
      get().copyLayers();
      get().deleteLayers(get().selectedLayerIds);
    },

    pasteLayers: () => {
      const { clipboard, canvasState } = get();
      if (clipboard.length === 0) return;
      const newLayers = duplicateLayers(clipboard, canvasState.layers.length);
      const newState = {
        ...canvasState,
        layers: [...canvasState.layers, ...newLayers],
      };
      set({
        canvasState: newState,
        selectedLayerIds: newLayers.map((l) => l.id),
      });
      get().saveToHistory();
    },

    duplicateSelected: () => {
      const { canvasState, selectedLayerIds } = get();
      if (selectedLayerIds.length === 0) return;
      const layers = canvasState.layers.filter((l) => selectedLayerIds.includes(l.id));
      const newLayers = duplicateLayers(layers, canvasState.layers.length);
      const newState = {
        ...canvasState,
        layers: [...canvasState.layers, ...newLayers],
      };
      set({
        canvasState: newState,
        selectedLayerIds: newLayers.map((l) => l.id),
      });
      get().saveToHistory();
    },

    // History
    saveToHistory: () => {
      const { canvasState } = get();
      history.push(canvasState);
      set({ canUndo: history.canUndo(), canRedo: history.canRedo() });
    },

    undo: () => {
      const state = history.undo();
      if (state) {
        set({
          canvasState: state,
          canUndo: history.canUndo(),
          canRedo: history.canRedo(),
        });
      }
    },

    redo: () => {
      const state = history.redo();
      if (state) {
        set({
          canvasState: state,
          canUndo: history.canUndo(),
          canRedo: history.canRedo(),
        });
      }
    },

    // View
    setZoom: (zoom) => set({ zoom: Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom)) }),
    zoomIn: () => {
      const { zoom } = get();
      set({ zoom: Math.min(MAX_ZOOM, zoom + 0.1) });
    },
    zoomOut: () => {
      const { zoom } = get();
      set({ zoom: Math.max(MIN_ZOOM, zoom - 0.1) });
    },
    setShowGrid: (show) => set({ showGrid: show }),
    setSnapToGrid: (snap) => set({ snapToGrid: snap }),
    resetView: () => set({ zoom: DEFAULT_ZOOM }),
    setActiveTool: (tool) => set({ activeTool: tool }),

    // Selection mask
    setSelection: (selection) => set({ selection }),

    // Project
    loadProject: (project) => {
      const state = project.canvas_state;
      history = new HistoryManager<CanvasState>(state, MAX_HISTORY_STATES);
      set({
        canvasState: state,
        selectedLayerIds: [],
        canUndo: false,
        canRedo: false,
      });
    },

    getProjectState: () => get().canvasState,
  }));
}
